/**
 * CR-6403: the Supplier Performance filter panel's option lists.
 *
 * Extracted from SupplierPerformanceFilters so that file stays under the
 * 500-line limit. The fetch body is moved verbatim — the Helix / non-Helix
 * branches, the ?context=customer_filter flip and every fallback are
 * unchanged; only its home has moved.
 */
import { useState, useEffect } from 'react';
import {
  coreAPI, purchasingAPI, suppliersAPI, fetchAllPages,
  Site, Company, CompanyGroup, Warehouse, CommodityGroup, CommodityType, SupplierProfile,
} from '../../../lib/api';
import { useViewKey } from '../../../contexts/ViewContext';

/** Extract array from either a flat array response or paginated {results} response */
function extractResults<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && 'results' in (data as Record<string, unknown>)) {
    return ((data as Record<string, unknown>)['results'] as T[]) || [];
  }
  return [];
}

export interface FilterOptions {
  divisions: CompanyGroup[];
  sites: Site[];
  supplierSites: Site[];
  warehouses: Warehouse[];
  supplierGroups: CompanyGroup[];
  commodityGroups: CommodityGroup[];
  commodityTypes: CommodityType[];
}

/** Loads every option list the filter panel renders, once per platform change. */
export default function useFilterOptions(platform: string | undefined): FilterOptions {
  const [divisions, setDivisions] = useState<CompanyGroup[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [supplierSites, setSupplierSites] = useState<Site[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [supplierGroups, setSupplierGroups] = useState<CompanyGroup[]>([]);
  const [commodityGroups, setCommodityGroups] = useState<CommodityGroup[]>([]);
  const [commodityTypes, setCommodityTypes] = useState<CommodityType[]>([]);
  const [_loading, setLoading] = useState(true);

  const viewKey = useViewKey();
  // Fetch filter options on mount and whenever the site switcher changes scope
  useEffect(() => {
    // A site switch starts a new load while the supplier walk may still be
    // running; only the newest load may write the options.
    let cancelled = false;
    const fetchFilterOptions = async () => {
      try {
        setLoading(true);
        const isHelixUser = platform === 'HELIX';

        // Fetch commodity data (available to all users)
        const commodityPromises = [
          purchasingAPI.getCommodityGroups({ enabled: true, page_size: 100 }).catch(() => ({ data: [] })),
          // Walk pages — the backend clamps page_size, so one request silently truncated.
          fetchAllPages<CommodityType>((page) => purchasingAPI.getCommodityTypes({ enabled: true, page_size: 100, page })).catch(() => [] as CommodityType[]),
        ] as const;

        if (isHelixUser) {
          // Helix users can access all data
          const [customerRes, supplierCompanies, groupsResponse, warehousesResponse, customerGroupsResponse, commodityGroupsResponse, commodityTypeRows] = await Promise.all([
            coreAPI.getCompanies({ company_type: 'customer', is_active: true }),
            // Walk pages — the backend clamps page_size, so one request silently truncated.
            // CR-8654: never offer a supplier the list hides (out of every KPI).
            fetchAllPages<Company>((page) => coreAPI.getCompanies({
              company_type: 'supplier', is_active: true, page_size: 100, page, kpi_included_only: true,
            })),
            coreAPI.getCompanyGroups({ is_active: true, company_type: 'supplier' }),
            coreAPI.getWarehouses({ is_active: true }),
            // CR-4783 round 2: divisions = customer-side CompanyGroups.
            coreAPI.getCompanyGroups({ is_active: true, company_type: 'customer' }).catch(() => ({ data: { results: [] } })),
            ...commodityPromises,
          ]);
          if (cancelled) return;
          const customerCompanies = customerRes.data.results || [];
          setSites(customerCompanies as unknown as Site[]);
          setSupplierSites(supplierCompanies as unknown as Site[]);
          setSupplierGroups(groupsResponse.data.results || []);
          setWarehouses(warehousesResponse.data.results || []);
          setCommodityGroups(extractResults(commodityGroupsResponse.data));
          setCommodityTypes(commodityTypeRows);
          const customerDivisions = customerGroupsResponse.data.results || [];
          setDivisions(customerDivisions.length > 1 ? customerDivisions : []);
        } else {
          // Non-Helix users use accessible endpoints only.
          // CR-4887: supplier-portal users want CUSTOMER hierarchy in the
          // Company Site / Division dropdowns. The Supplier Performance
          // page mirrors the Dashboard's filter panel; flip the accessible
          // endpoints with ?context=customer_filter for the same reason.
          const isSupplierPortal = platform === 'SUPPLIER';
          const filterContext = isSupplierPortal ? { context: 'customer_filter' as const } : undefined;
          const [sitesResponse, groupsResponse, supplierPages, commodityGroupsResponse, commodityTypeRows, warehousesResponse] = await Promise.all([
            coreAPI.getAccessibleSites(filterContext).catch(() => ({ data: [] })),
            coreAPI.getSupplierGroupsForFilter(filterContext).catch(() => ({ data: { results: [] } })),
            // Walk pages — the supplier cap is 1000 a page, so one request
            // left every supplier past the first thousand out of the picker.
            fetchAllPages<SupplierProfile>((page) => (cancelled
              ? Promise.reject(new Error('Superseded by a newer filter load'))
              : suppliersAPI.getProfiles({
                page_size: 1000, page, minimal: true, kpi_included_only: 'true',
              }))).catch(() => [] as SupplierProfile[]),
            ...commodityPromises,
            // CR-6312 rework: customer users get their own sites' warehouses
            // (the endpoint scopes to own_site_ids for non-Helix). The old
            // hard-coded empty array predates that scoping. Suppliers can
            // only 403 here, so resolve to none without asking.
            isSupplierPortal
              ? Promise.resolve({ data: { results: [] as Warehouse[] } })
              : coreAPI.getWarehouses({ is_active: true }).catch(() => ({ data: { results: [] as Warehouse[] } })),
          ]);
          if (cancelled) return;
          const accessibleSites = Array.isArray(sitesResponse.data) ? sitesResponse.data : [];
          setSites(accessibleSites);
          // CR-6403: Include country from company_details for Supplier Country filter
          // Each page is cached on its own, so one fetched later can repeat a row.
          const supplierProfiles = Array.from(new Map(supplierPages.map((s) => [s.id, s])).values());
          const supplierCompanies = supplierProfiles.map(s => ({
            id: s.company_id,
            name: s.company_name,
            country: (s as unknown as { company_details?: { country?: string } }).company_details?.country || '',
          })) as unknown as Site[];
          setSupplierSites(supplierCompanies);
          setSupplierGroups(groupsResponse.data.results || []);
          setWarehouses(warehousesResponse.data.results || []);
          setCommodityGroups(extractResults(commodityGroupsResponse.data));
          setCommodityTypes(commodityTypeRows);
          // CR-6403: Derive divisions from accessible sites' group info,
          // same pattern as Dashboard.tsx. Each site has group_id and
          // group_name fields — extract unique divisions from them.
          const divMap = new Map<number, string>();
          for (const site of accessibleSites) {
            const rec = site as unknown as Record<string, unknown>;
            const groupId = rec['group_id'] as number | undefined;
            const groupName = rec['group_name'] as string | undefined;
            if (groupId && groupName) {
              divMap.set(groupId, groupName);
            }
          }
          const derivedDivisions = Array.from(divMap.entries())
            .map(([id, name]) => ({ id, name } as CompanyGroup))
            .sort((a, b) => a.name.localeCompare(b.name));
          setDivisions(derivedDivisions.length > 1 ? derivedDivisions : []);
        }
      } catch (error) {
        // Silently handle - filters will just be empty
        console.debug('Filter options not available');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchFilterOptions();
    return () => { cancelled = true; };
  }, [platform, viewKey]);

  return { divisions, sites, supplierSites, warehouses, supplierGroups, commodityGroups, commodityTypes };
}
